import * as React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, Activity, Database, BarChart3, Clock, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useToast } from '@/shared/hooks/useToast';

interface SystemMetrics {
  totalUsers: number;
  totalWorkspaces: number;
  totalRepositories: number;
  totalAnalyses: number;
}

interface AnalysisJobStats {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  total: number;
}

interface UserMetrics {
  activeUsers: {
    last24h: number;
    last7d: number;
    last30d: number;
  };
  recentSignups: Array<{
    id: string;
    email: string;
    name: string;
    emailVerified: boolean;
    createdAt: string;
  }>;
}

interface OverviewMetrics {
  activeUsers: {
    count: number;
    timeframe: string;
  };
  system: SystemMetrics;
}

export function ActivityDashboard(): React.JSX.Element {
  const [loading, setLoading] = React.useState(true);
  const [overview, setOverview] = React.useState<OverviewMetrics | null>(null);
  const [userMetrics, setUserMetrics] = React.useState<UserMetrics | null>(null);
  const [analysisStats, setAnalysisStats] = React.useState<AnalysisJobStats | null>(null);
  const { toast } = useToast();

  const loadMetrics = React.useCallback(async () => {
    try {
      setLoading(true);
      const [overviewData, userData, analysisData] = await Promise.all([
        api.get<OverviewMetrics>('/admin/metrics/overview?timeframe=24h'),
        api.get<UserMetrics>('/admin/metrics/users'),
        api.get<{ stats: AnalysisJobStats }>('/admin/metrics/analysis'),
      ]);

      setOverview(overviewData);
      setUserMetrics(userData);
      setAnalysisStats(analysisData.stats);
    } catch (error) {
      toast({
        title: 'Error loading metrics',
        description: error instanceof Error ? error.message : 'Failed to load dashboard metrics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  React.useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Activity Dashboard</h1>
          <p className="text-muted-foreground">System metrics and user activity overview</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4 rounded" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-7 w-16" />
                <Skeleton className="h-3 w-32 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Activity Dashboard</h1>
        <p className="text-muted-foreground">System metrics and user activity overview</p>
      </div>

      {/* System Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users (24h)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.activeUsers.count ?? 0}</div>
            <p className="text-xs text-muted-foreground">Last 24 hours</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.system.totalUsers ?? 0}</div>
            <p className="text-xs text-muted-foreground">All registered users</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Repositories</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.system.totalRepositories ?? 0}</div>
            <p className="text-xs text-muted-foreground">Total uploaded repos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Analyses</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overview?.system.totalAnalyses ?? 0}</div>
            <p className="text-xs text-muted-foreground">All time analyses</p>
          </CardContent>
        </Card>
      </div>

      {/* Analysis Job Status */}
      {analysisStats && (
        <Card>
          <CardHeader>
            <CardTitle>Analysis Job Status</CardTitle>
            <CardDescription>Current analysis queue status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium">Pending</p>
                  <p className="text-2xl font-bold">{analysisStats.pending}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                <div>
                  <p className="text-sm font-medium">Processing</p>
                  <p className="text-2xl font-bold">{analysisStats.processing}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium">Completed</p>
                  <p className="text-2xl font-bold">{analysisStats.completed}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-sm font-medium">Failed</p>
                  <p className="text-2xl font-bold">{analysisStats.failed}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-600" />
                <div>
                  <p className="text-sm font-medium">Total</p>
                  <p className="text-2xl font-bold">{analysisStats.total}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Activity */}
      {userMetrics && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Active Users</CardTitle>
              <CardDescription>User activity over different timeframes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Last 24 hours</span>
                  <Badge variant="secondary">{userMetrics.activeUsers.last24h}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Last 7 days</span>
                  <Badge variant="secondary">{userMetrics.activeUsers.last7d}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Last 30 days</span>
                  <Badge variant="secondary">{userMetrics.activeUsers.last30d}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Signups</CardTitle>
              <CardDescription>Latest user registrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {userMetrics.recentSignups.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No recent signups</p>
                ) : (
                  userMetrics.recentSignups.slice(0, 5).map((user) => (
                    <div key={user.id} className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.emailVerified ? (
                          <Badge variant="outline" className="text-xs">
                            Verified
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            Unverified
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
